import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";

const TermsOfServicePage = () => {
  const currentDate = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="min-h-screen bg-ink-50" data-testid="terms-page">
      {/* Header */}
      <div className="bg-white border-b border-ink-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-ink-500 hover:text-ink-950 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to XEA
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-coral-500 flex items-center justify-center">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-ink-950">Terms of Service</h1>
            <p className="text-ink-500">Last updated: {currentDate}</p>
          </div>
        </div>

        <div className="prose prose-slate max-w-none">
          <div className="space-y-8 text-ink-700 leading-relaxed">

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">1. Acceptance of Terms</h2>
              <p>
                By accessing, registering for, or using XEA (the
                &quot;Service&quot;, &quot;Platform&quot;, &quot;we&quot;,
                &quot;us&quot;) — including the web app, the Progressive Web
                App (PWA), and native iOS / Android distributions — you
                agree to be legally bound by these Terms of Service and our{" "}
                <Link to="/privacy" className="text-coral-500 underline">Privacy Policy</Link>{" "}
                and{" "}
                <Link to="/cookies" className="text-coral-500 underline">Cookie Policy</Link>.
                If you do not agree, you must not use the Service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">2. The Service</h2>
              <p>
                XEA is an AI-powered creative platform. The Service is
                organized into four studio modes (&quot;realms&quot;):
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  <strong>Marketing</strong> — AI image &amp; video
                  generation from product links and prompts, bulk creative
                  generation, content calendar, scheduled posting (where
                  enabled), and store-URL integration.
                </li>
                <li>
                  <strong>Designing</strong> — standalone design assets
                  (logos, posters, illustrations, vector, 3D) without a
                  store.
                </li>
                <li>
                  <strong>CGI / FX Lab</strong> — multi-clip timeline
                  editor, CGI effects, &quot;Director Polish&quot; powered
                  by Claude Sonnet 4.5, voiceover muxing, SFX overlays, and
                  brand-frame compositing.
                </li>
                <li>
                  <strong>Movie Mode</strong> — long-form cinematic films
                  (up to the duration permitted by your tier) planned by
                  Claude Sonnet 4.5, with multi-shot Kling rendering,
                  ElevenLabs narration, sidechain-ducked music, and ASS
                  subtitles.
                </li>
              </ul>
              <p className="mt-2">Cross-cutting features include:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Token-based usage (&quot;XEA tokens&quot;) with automatic refunds on failed jobs</li>
                <li>The <strong>Community</strong> feed (opt-in sharing) filtered by realm</li>
                <li>The <strong>Weekend Top 20</strong> leaderboard with weekly token rewards</li>
                <li>The <strong>Creator Tipping</strong> system (paid tiers only)</li>
                <li>Per-mode animated onboarding tours</li>
                <li>Token Activity Ledger (full audit of every token credit and debit)</li>
                <li>Scheduled email reminders at a user-chosen time</li>
                <li>Integrations with select social platforms (subject to platform approval)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">3. Account Eligibility</h2>
              <p>
                You must be at least 18 years old to use XEA. By registering,
                you warrant that the information you provide is accurate and
                that you are legally authorised to enter into this
                agreement. You are responsible for safeguarding your password
                and for any activity on your account. Brute-force attempts
                will trigger temporary account lockouts. You may not share
                an account, sell an account, or sub-license access.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">4. Plans, Tokens &amp; Free Tier</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Free tier:</strong> a one-time allocation of XEA
                  tokens. There is no monthly refresh. You may claim a
                  one-time bonus extension from the Tokens page. After
                  exhausting these tokens you must upgrade or purchase a
                  token pack to continue generating.
                </li>
                <li>
                  <strong>Paid tiers (Starter, Premium, Pro, Platinum):</strong>{" "}
                  monthly token allowances, advanced features, higher
                  Movie-Mode duration caps, FX-Lab &quot;Director
                  Polish&quot;, voiceovers, and the ability to send tips on
                  the Weekend Top 20 leaderboard (Pro &amp; Platinum).
                  Pricing and tier limits are shown on the Pricing /
                  Subscription page and may be changed prospectively with
                  notice.
                </li>
                <li>
                  <strong>Token packs:</strong> one-off purchases that top
                  up your balance immediately.
                </li>
                <li>
                  <strong>Admin gifts:</strong> XEA staff may grant tokens or
                  upgrade subscription tiers as part of marketing, support,
                  or refund-substitute actions. Gifted tokens are subject to
                  the same forfeiture rules as purchased tokens.
                </li>
                <li>
                  <strong>Automatic refunds:</strong> if a generation
                  irrecoverably fails on our side, the tokens charged are
                  automatically credited back to your balance and recorded
                  in your Token Activity Ledger.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">5. Payments &amp; No-Refund Policy</h2>
              <p>
                Payments are processed via PayPal, Stripe (where active),
                and supported crypto channels (USDT, BTC, ETH, and others).{" "}
                <strong>All payments — subscription fees, token packs,
                tips, and any other paid feature — are final and
                non-refundable.</strong> Before any checkout you must
                explicitly confirm the &quot;Non-Refundable&quot; disclosure
                modal. Unauthorised chargebacks may result in account
                suspension and forfeiture of all balances.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">6. Community Feed (User-Generated Content)</h2>
              <p>
                Sharing to the Community is entirely opt-in. By publishing
                a creation you grant XEA a worldwide, non-exclusive,
                royalty-free, sub-licensable licence to host, display, and
                feature it within the Service (including the Weekend Top 20
                carousel, realm-filtered tabs, and admin-curated
                moodboard). You retain ownership of your content. You may
                unshare or delete a post at any time from your &quot;My
                community posts&quot; page.
              </p>
              <p className="mt-2">
                You must not share content that is unlawful, hateful,
                sexually explicit, infringing, deceptive, or that contains
                personal data of third parties without their consent. Posts
                are screened by an AI safety filter; flagged content is
                blocked or placed under review. Three or more user reports
                automatically remove a post pending admin review. XEA may
                remove any content at its sole discretion.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">7. Weekend Top 20 &amp; Tipping</h2>
              <p>
                Each Saturday at 00:00 (Europe/Nicosia) we automatically
                rank community posts created during the same Monday–Friday
                by total <em>likes + comments</em> (equal weight). The 20
                highest-scoring posts per realm — and an overall combined
                ranking — are pinned to the public feed and become
                eligible to receive tips until Sunday 12:00 (Europe/Nicosia).
                The ranking is computed by automated software based on
                objective engagement metrics. Internal test / staff accounts
                are flagged <code>exclude_from_public</code> and cannot win.
                XEA admins may manually open or close the leaderboard
                window if operational circumstances require it; any such
                manual change is logged.
              </p>
              <p className="mt-2">
                <strong>Tipping mechanics:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Only Pro and Platinum members may send tips.</li>
                <li>Tips range from 5 to 500 XEA tokens per transaction.</li>
                <li>You may not tip your own post.</li>
                <li>
                  100% of the tipped amount is credited to the
                  recipient&apos;s separate <strong>Earnings Balance</strong>.
                  The recipient may withdraw any portion to their main
                  token balance at any time.
                </li>
                <li>
                  XEA tokens (including tipped, earned, gifted, and
                  withdrawn tokens) are <strong>in-app credits with no
                  monetary value</strong>. They are not redeemable for cash,
                  are not legal tender, and cannot be transferred outside
                  XEA. They may not be sold, traded, or used as security.
                </li>
                <li>Tips, like all payments, are final and non-refundable.</li>
                <li>
                  XEA reserves the right to void tokens and earnings
                  obtained through abuse, fraud, automation,
                  multi-accounting, or any violation of these Terms.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">8. Acceptable Use</h2>
              <p>You agree not to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Use the Service for any illegal purpose</li>
                <li>Generate content that violates intellectual-property or publicity rights (including unauthorized deepfakes or impersonation of real people)</li>
                <li>Produce sexually explicit, nude, hateful, violent, gore, terrorist, self-harm-promoting, or deceptive material</li>
                <li>Generate political-disinformation, election-manipulation, or election-influence material</li>
                <li>Misrepresent the AI origin of generated material where law requires disclosure</li>
                <li>Attempt to reverse-engineer, scrape, jailbreak prompts, or otherwise interfere with the Service or its safety models</li>
                <li>Create multiple accounts to circumvent the free-tier allocation, win the Top 20, accumulate tips, or evade a ban</li>
                <li>Use Movie Mode, FX Lab voiceovers, or any cloning capability to impersonate any real person without their explicit consent</li>
                <li>Resell access, tokens, earnings, voiceovers, movies, or any feature of the Service</li>
                <li>Submit prompts, captions, overlays, scripts, or uploaded media that target minors in any way</li>
              </ul>
            </section>

            <section className="rounded-2xl border-2 border-red-500/40 bg-red-50/60 p-5">
              <h2 className="text-xl font-bold text-red-700 mb-3">
                8.1 Zero-Tolerance: Sexual Content — Immediate Permanent Ban
              </h2>
              <p className="text-ink-950 font-medium">
                XEA exists to protect children. <strong>Any attempt</strong>{" "}
                to generate, upload, prompt, caption, narrate, voiceover,
                script, or share sexual, nude, pornographic, fetish, or
                otherwise sexually explicit content — across <em>any</em>{" "}
                studio (Marketing, Designing, CGI, Movie), <em>any</em> tool
                (image, video, composer, timeline, FX Lab, Movie planner,
                voiceover, comment, community caption), and through{" "}
                <em>any</em> field (prompt, title, text overlay, edit
                prompt, negative prompt, narration line, subtitle, music
                lyric) — triggers an <strong>immediate, permanent ban</strong>{" "}
                of your account.
              </p>
              <p className="mt-2">
                There is <strong>no warning</strong>, no grace period, no
                tiered escalation, and <strong>no appeal</strong>. Your
                tokens, earnings balance, gifted balances, active
                subscription, referrals, and pending payouts are all
                forfeited without refund. We may preserve a record of the
                violating prompt, IP address, device fingerprint, and
                account identifiers for as long as is necessary to enforce
                this policy across re-registration attempts. We will
                cooperate with law enforcement where the attempt involves or
                appears to involve a minor.
              </p>
              <p className="mt-2 text-sm text-ink-700">
                Enforcement is automated and applies to admins-in-training,
                creators, paid subscribers, gifted accounts, and trial users
                alike. We are not an adult-content platform. If you came
                here looking for an adult-content generator, please close
                this tab.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">9. AI-Generated Content &amp; Moderation</h2>
              <p>
                Outputs from the Service are produced by machine-learning
                models provided by us and third-party vendors — including
                OpenAI, Anthropic (Claude Sonnet 4.5), Google Gemini,
                DeepSeek, Kling AI, ElevenLabs, Mubert, and Jamendo. We do
                not guarantee accuracy, originality, factual correctness,
                voice-likeness rights, music-license clearance for derivative
                use, or fitness for any particular purpose. You are solely
                responsible for reviewing outputs and obtaining any
                additional rights required before publishing them anywhere
                — including outside XEA.
              </p>
              <p className="mt-2">
                Prompts, captions, titles, text overlays, narration scripts,
                voiceover transcripts, and uploads may be screened by
                automated moderation. As stated in §8.1, attempts to
                generate sexual content result in immediate permanent
                account termination. Other policy violations (hateful,
                violent, deceptive content, misuse of intellectual property,
                impersonation, or disinformation) may result in token
                forfeiture, suspension, or permanent termination of your
                account, at our sole discretion.
              </p>
              <p className="mt-2">
                <strong>Voiceovers &amp; voice cloning:</strong> if you
                upload a voice sample or use any cloning feature, you
                warrant that you own all rights to that voice or have the
                speaker&apos;s explicit, contemporaneous, written consent.
                We may require proof of consent before releasing renders.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">10. Communications &amp; Email Reminders</h2>
              <p>
                We send transactional emails (receipts, password resets,
                tip receipts, account-security notices, refund credits,
                ban / suspension notices, weekly leaderboard digests).
                These are sent for as long as you maintain an account,
                regardless of marketing preferences.
              </p>
              <p className="mt-2">
                XEA also offers an optional{" "}
                <strong>Daily Email Reminder</strong> sent at a time you
                select in Settings. This replaces the legacy browser
                push-notification system, which has been retired. You can
                turn the daily email on or off at any time from the
                Settings page. Marketing emails (product news,
                announcements) may be opted out via the unsubscribe link in
                the email footer. Native-app push (Capacitor) is opt-in at
                install time and can be revoked from your OS settings.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">11. Native App (PWA / Capacitor)</h2>
              <p>
                XEA is available as a Progressive Web App (PWA) and as
                native iOS / Android shells packaged with Capacitor. The
                native shells use the same backend and apply the same
                Terms. The Service is also subject to the App Store Review
                Guidelines and Google Play Developer Program Policies, and
                in case of conflict the stricter requirement applies. You
                are responsible for complying with your store-of-purchase
                terms when buying via in-app purchase channels (where
                offered).
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">12. Termination</h2>
              <p>
                You may close your account at any time from Settings. We
                may suspend or terminate your account, with or without
                notice, for violations of these Terms, abuse of the
                Service, fraud, unauthorized chargebacks, or where required
                by law. On termination, your tokens, earnings balance,
                referrals, and pending payouts are{" "}
                <strong>forfeited</strong> without refund. We may retain
                certain data as required by law (see Privacy Policy §7).
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">13. Disclaimers &amp; Liability</h2>
              <p>
                The Service is provided &quot;AS IS&quot; and &quot;AS
                AVAILABLE&quot; without warranties of any kind, express or
                implied. AI outputs may be inaccurate, hallucinated, or
                offensive — review every output before publishing. To the
                maximum extent permitted by law, XEA disclaims liability for
                indirect, incidental, special, consequential, or punitive
                damages (including lost profits, lost data, or reputational
                harm). Our total liability for any claim is capped at the
                amount you paid us in the 30 days preceding the event
                giving rise to the claim, or USD 50, whichever is greater.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">14. Indemnification</h2>
              <p>
                You agree to defend, indemnify, and hold harmless XEA, its
                officers, employees, contractors, and vendors from and
                against any claims, damages, losses, liabilities, and
                expenses (including reasonable attorneys&apos; fees)
                arising out of your content, your use of the Service, your
                violation of these Terms, or your violation of any
                third-party right (including IP, publicity, or privacy
                rights).
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">15. Changes to These Terms</h2>
              <p>
                We may update these Terms from time to time. Material
                changes will be announced via email and/or an in-app notice
                at least 14 days before they take effect. Continued use of
                the Service after the effective date constitutes acceptance
                of the revised Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">16. Governing Law &amp; Contact</h2>
              <p>
                These Terms are governed by the laws of Cyprus, without
                regard to conflict-of-laws principles. Where a consumer
                resides in the EU, mandatory consumer-protection rules of
                their habitual residence still apply. For questions,
                contact us at{" "}
                <a href="mailto:legal@xe-a.com" className="text-coral-500 underline">
                  legal@xe-a.com
                </a>
                .
              </p>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
